import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserList } from '../components/UserList';
import { UserForm } from '../components/UserForm';
import { useCreateUser, useUpdateUser } from '../hooks/useUsers';
import type { User, CreateUserRequest, UpdateUserRequest } from '../users.api';

export function UserManagementPage() {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | undefined>();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingUser(undefined);
    setShowForm(true);
  };

  const handleSubmit = (data: CreateUserRequest | UpdateUserRequest) => {
    if (editingUser) {
      updateUser.mutate(
        { userId: editingUser.id, data },
        { onSuccess: () => setShowForm(false) },
      );
    } else {
      createUser.mutate(data as CreateUserRequest, {
        onSuccess: () => setShowForm(false),
      });
    }
  };

  return (
    <div data-testid="user-management-page">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1
            data-testid="page-title"
            style={{
              fontSize: '1.25rem',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'var(--foreground-heading)',
            }}
          >
            {t('users.title')}
          </h1>
          <p
            className="mt-[3px]"
            style={{ fontSize: '0.8125rem', color: 'var(--foreground-muted)' }}
          >
            {t('users.subtitle')}
          </p>
        </div>
        {!showForm && (
          <button
            data-testid="create-user-button"
            onClick={handleCreate}
            className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-[0.8125rem] font-medium text-white transition-all duration-100"
            style={{
              backgroundColor: 'var(--primary)',
              border: '1px solid var(--primary)',
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '15px' }} aria-hidden="true">
              add
            </span>
            {t('users.createUser')}
          </button>
        )}
      </div>

      {showForm ? (
        <div
          className="rounded-xl p-5"
          style={{
            backgroundColor: 'var(--background)',
            border: '1px solid var(--border-soft)',
            boxShadow: 'var(--card-shadow)',
          }}
        >
          <h2
            className="mb-4 text-[0.8125rem] font-semibold"
            style={{ color: 'var(--foreground-heading)' }}
          >
            {editingUser ? t('users.editUser') : t('users.createUser')}
          </h2>
          <UserForm
            user={editingUser}
            onSubmit={handleSubmit}
            onCancel={() => setShowForm(false)}
            isLoading={createUser.isPending || updateUser.isPending}
          />
        </div>
      ) : (
        <UserList onEdit={handleEdit} />
      )}
    </div>
  );
}
